/**
 * SSE Manager
 * Wraps native EventSource with event parsing, delta buffering,
 * and exponential backoff reconnection.
 *
 * Reconnection behaviour: max 3 attempts at 1 s, 2 s, 4 s.
 * After 3 failures a terminal 'error' event is emitted and no
 * further reconnects are attempted. A fresh connect() call resets
 * the counter.
 *
 * Token injection: provide an async `getToken` function via options.
 * When omitted, falls back to `window.__MOCK_TOKEN__` (dev only).
 */

import { parseSSEPayload, type SSEEvent } from '../utils/event-parser';
import { createDeltaBuffer } from '../utils/delta-buffer';
import { logger } from '../utils/logger';

const TX_API_BASE = import.meta.env.VITE_TX_BFF_URL || '/tx-api';

const VALID_EVENT_TYPES = [
  'textDelta',
  'thinkingStart',
  'thinkingEnd',
  'artifactUpdate',
  'artifactDelete',
  'sessionTerminated',
  'session_terminated',
  'error',
  'statusUpdate',
  'permission.asked',
  'question.asked',
  'message.part.updated',
  'turn_warning',
  'done',
  'summaryGenerating',
  'summaryComplete',
  'summaryFailed',
];

export type SSEConnectionState = 'connecting' | 'open' | 'closed' | 'error';

export type SSEEventCallback<T extends SSEEvent> = (event: T) => void;

export interface SSEManagerOptions {
  baseUrl?: string;
  maxReconnectDelayMs?: number;
  initialReconnectDelayMs?: number;
  /** Maximum number of reconnect attempts before giving up (default: 3). */
  maxRetries?: number;
  /**
   * Async function that returns the auth token to append as ?token=...
   * Falls back to window.__MOCK_TOKEN__ when not provided.
   */
  getToken?: () => Promise<string | null>;
}

export class SSEManager {
  private sessionId: string;
  private baseUrl: string;
  private eventSource: EventSource | null = null;
  private listeners: Map<string, Set<SSEEventCallback<SSEEvent>>> = new Map();
  private deltaBuffer = createDeltaBuffer((text: string) => {
    this.emit('textDelta', { type: 'textDelta', delta: text });
  });
  private reconnectDelayMs: number;
  private initialReconnectDelayMs: number;
  private maxReconnectDelayMs: number;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private shouldReconnect = false;
  private connectionState: SSEConnectionState = 'closed';
  private retryCount = 0;
  private maxRetries: number;
  private getToken: () => Promise<string | null>;

  constructor(sessionId: string, options: SSEManagerOptions = {}) {
    this.sessionId = sessionId;
    this.baseUrl = options.baseUrl ?? TX_API_BASE;
    this.initialReconnectDelayMs = options.initialReconnectDelayMs ?? 1000;
    this.maxReconnectDelayMs = options.maxReconnectDelayMs ?? 30000;
    this.reconnectDelayMs = this.initialReconnectDelayMs;
    this.maxRetries = options.maxRetries ?? 3;
    this.getToken = options.getToken ?? (() => Promise.resolve(
      (window as any).__MOCK_TOKEN__ ?? null
    ));
  }

  get state(): SSEConnectionState {
    return this.connectionState;
  }

  connect(): void {
    if (this.eventSource) {
      logger.warn('SSEManager: already connected');
      return;
    }

    this.shouldReconnect = true;
    this.retryCount = 0;
    this.reconnectDelayMs = this.initialReconnectDelayMs;
    this.connectionState = 'connecting';
    this._openConnection();
  }

  private _openConnection(): void {
    this.getToken().then((token) => {
      let url = `${this.baseUrl}/api/sessions/${this.sessionId}/events`;

      if (token) {
        url += `?token=${encodeURIComponent(token)}`;
      }

      this.eventSource = new EventSource(url, { withCredentials: true });

      this.eventSource.onopen = () => {
        this.connectionState = 'open';
        this.reconnectDelayMs = this.initialReconnectDelayMs;
        this.retryCount = 0;
        this.emit('open', undefined as unknown as SSEEvent);
      };

      // Named event listeners
      VALID_EVENT_TYPES.forEach((type) => {
        this.eventSource?.addEventListener(type, (event: MessageEvent) => {
          try {
            const payload = JSON.parse(event.data);
            const sseEvent = parseSSEPayload(type, payload);
            if (sseEvent) {
              if (sseEvent.type === 'textDelta') {
                this.deltaBuffer.push(sseEvent.delta);
              } else if (sseEvent.type === 'message.part.updated') {
                this.deltaBuffer.push(sseEvent.part.text);
              } else {
                this.emit(sseEvent.type, sseEvent);
              }

              if (sseEvent.type === 'sessionTerminated') {
                this.disconnect();
              }
            }
          } catch (err) {
            logger.error(`SSEManager: Failed to parse event data for ${type}`, err);
          }
        });
      });

      // Fallback for unnamed events
      this.eventSource.onmessage = (rawEvent: MessageEvent) => {
        try {
          const payload = JSON.parse(rawEvent.data);
          const event = parseSSEPayload('textDelta', payload);
          if (event && event.type === 'textDelta') {
            this.deltaBuffer.push(event.delta);
          }
        } catch {
          // Ignore if not JSON
        }
      };

      this.eventSource.onerror = () => {
        this.connectionState = 'error';
        this.emit('error', { type: 'error', message: 'Connection error' } as SSEEvent);
        this._scheduleReconnect();
      };
    }).catch((err) => {
      logger.error('SSEManager: Failed to acquire auth token', err);
      this.connectionState = 'error';
      this.emit('error', { type: 'error', message: 'Failed to acquire auth token' } as SSEEvent);
    });
  }

  private _scheduleReconnect(): void {
    if (!this.shouldReconnect) return;

    // Enforce maximum retry limit
    if (this.retryCount >= this.maxRetries) {
      this.shouldReconnect = false;
      this.connectionState = 'error';
      logger.warn(
        'SSEManager: Max reconnect attempts reached, giving up',
        { sessionId: this.sessionId, maxRetries: this.maxRetries }
      );
      this.emit('error', {
        type: 'error',
        message: 'Max reconnect attempts reached',
      } as SSEEvent);
      return;
    }

    this.retryCount++;
    this._cleanupEventSource();

    logger.info(
      'SSEManager: Scheduling reconnect',
      { sessionId: this.sessionId, attempt: this.retryCount, delayMs: this.reconnectDelayMs }
    );

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this._openConnection();
    }, this.reconnectDelayMs);

    this.reconnectDelayMs = Math.min(
      this.reconnectDelayMs * 2,
      this.maxReconnectDelayMs
    );
  }

  disconnect(): void {
    this.shouldReconnect = false;
    this._cleanupEventSource();
    this.deltaBuffer.flush();
    this.connectionState = 'closed';
    this.emit('closed', undefined as unknown as SSEEvent);
  }

  private _cleanupEventSource(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
  }

  on<T extends SSEEvent>(eventType: SSEEvent['type'], callback: SSEEventCallback<T>): void {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }
    this.listeners.get(eventType)!.add(callback as SSEEventCallback<SSEEvent>);
  }

  off<T extends SSEEvent>(eventType: SSEEvent['type'], callback: SSEEventCallback<T>): void {
    const set = this.listeners.get(eventType);
    if (set) {
      set.delete(callback as SSEEventCallback<SSEEvent>);
    }
  }

  private emit(eventType: string, event: SSEEvent): void {
    const set = this.listeners.get(eventType);
    if (set) {
      set.forEach((cb) => {
        try {
          cb(event);
        } catch (err) {
          logger.error('SSEManager: listener error', err);
        }
      });
    }
  }
}
